import { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Table, { Badge, TableLink } from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface EmailTemplate {
  id: string;
  key: string;
  subject: string;
  type: string;
  content: string;
  state: string;
}

const EMAIL_TYPES = ['Onboarding', 'Financeiro', 'Marketing', 'Sistema', 'Notificação'];

export default function Emails() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState({
    key: '',
    subject: '',
    type: 'Onboarding',
    content: '',
    state: 'active'
  });
  const [saving, setSaving] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [showPreviewInEditor, setShowPreviewInEditor] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAdminAuth('/api/admin/email-templates');
      if (response.ok) {
        const data = await response.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setToast({ message: 'Erro ao carregar templates', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingTemplate(null);
    setFormData({
      key: '',
      subject: '',
      type: 'Onboarding',
      content: '',
      state: 'active'
    });
    setIsModalOpen(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      key: template.key,
      subject: template.subject,
      type: template.type,
      content: template.content,
      state: template.state
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const url = editingTemplate 
        ? `/api/admin/email-templates/${editingTemplate.key}`
        : '/api/admin/email-templates';
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetchWithAdminAuth(url, {
        method,
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setToast({ message: 'Template guardado com sucesso!', type: 'success' });
        setIsModalOpen(false);
        fetchTemplates();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save');
      }
    } catch (error) {
      setToast({ message: error instanceof Error ? error.message : 'Erro ao guardar template', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!confirm('Tem certeza que deseja eliminar este template?')) return;
    
    try {
      const response = await fetchWithAdminAuth(`/api/admin/email-templates/${key}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setToast({ message: 'Template eliminado com sucesso!', type: 'success' });
        fetchTemplates();
      }
    } catch (error) {
      setToast({ message: 'Erro ao eliminar template', type: 'error' });
    }
  };

  const columns = [
    { header: 'Chave', key: 'key' },
    { header: 'Assunto', key: 'subject' },
    { header: 'Tipo', key: 'type' },
    { 
      header: 'Estado', 
      key: 'state', 
      render: (val: string) => (
        <Badge variant={val === 'active' ? 'success' : 'danger'}>
          {val === 'active' ? 'Ativo' : 'Inativo'}
        </Badge>
      )
    },
    { 
      header: '', 
      key: 'actions', 
      render: (_val: any, row: EmailTemplate) => (
        <div className="flex gap-2">
          <TableLink onClick={() => setPreviewTemplate(row)}>Pré-visualizar</TableLink>
          <TableLink onClick={() => handleEdit(row)}>Editar</TableLink>
          <TableLink onClick={() => handleDelete(row.key)}>Eliminar</TableLink>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card title="Templates de Email" subtitle="A carregar...">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Templates de Email"
        subtitle="Mensagens automáticas de boas-vindas, pagamento confirmado e saldo a terminar."
        action={<Button variant="primary" onClick={handleNew}>+ Novo template</Button>}
      >
        <Table columns={columns} data={templates} />
        {templates.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum template encontrado. Clique em "+ Novo template" para criar.
          </div>
        )}
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingTemplate ? 'Editar Template' : 'Novo Template'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Chave</label>
            <input
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              disabled={!!editingTemplate}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-50"
              placeholder="ex: welcome, payment_confirm"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Assunto</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="Assunto do email"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Tipo</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              >
                {EMAIL_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Estado</label>
              <select
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              >
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm text-gray-300">Conteúdo HTML</label>
              <button
                type="button"
                onClick={() => setShowPreviewInEditor(!showPreviewInEditor)}
                className="text-xs text-accent hover:underline"
              >
                {showPreviewInEditor ? '✎ Código' : '👁 Pré-visualizar'}
              </button>
            </div>
            {showPreviewInEditor ? (
              <div className="w-full rounded-lg border border-gray-700 overflow-hidden bg-white" style={{ minHeight: '200px' }}>
                <iframe
                  srcDoc={formData.content || '<p style="color:#999;padding:20px">Sem conteúdo para pré-visualizar</p>'}
                  title="Preview"
                  className="w-full border-0"
                  style={{ minHeight: '200px' }}
                  sandbox="allow-same-origin"
                />
              </div>
            ) : (
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white min-h-[200px] font-mono text-sm"
                placeholder="<html>...</html>"
              />
            )}
            <p className="text-xs text-gray-500 mt-1">
              Variáveis disponíveis: {'{{userName}}'}, {'{{userEmail}}'}, {'{{amount}}'}, {'{{date}}'}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'A guardar...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        title={`Pré-visualizar: ${previewTemplate?.subject || ''}`}
      >
        <div className="space-y-3">
          <div className="flex gap-4 text-xs text-gray-400">
            <span>Chave: <strong className="text-gray-200">{previewTemplate?.key}</strong></span>
            <span>Tipo: <strong className="text-gray-200">{previewTemplate?.type}</strong></span>
          </div>
          <div className="w-full rounded-lg border border-gray-700 overflow-hidden bg-white" style={{ minHeight: '300px' }}>
            <iframe
              srcDoc={previewTemplate?.content || '<p style="color:#999;padding:20px">Sem conteúdo</p>'}
              title="Email Preview"
              className="w-full border-0"
              style={{ minHeight: '300px' }}
              sandbox="allow-same-origin"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => {
              if (previewTemplate) handleEdit(previewTemplate);
              setPreviewTemplate(null);
            }}>✎ Editar</Button>
            <Button onClick={() => setPreviewTemplate(null)}>Fechar</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
